<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { useAdminPortal } from "@/services/fronteggTheme";
import { useToast } from "vue-toastification";
import {
  Menu,
  X,
  Folder,
  SquareLibrary,
  Shield,
  ChartPie,
  Boxes,
  Layers,
  Truck,
  Recycle,
  FileText,
  History,
  GraduationCap,
  SlidersHorizontal,
  UserCircle,
  LogOut,
} from "@lucide/vue";

import router from "../../router/router";
import { mobileMenuStore } from "../../stores/Utils/Mobile/MobileMenu";
import { useAuthStateStore } from "../../stores/Utils/auth/AuthState";
import { useProject } from "@/stores/project/project.store";
import { useSpeckleStore } from "@/stores/speckle/speckle.store";

import { clearLocalStorage } from "../../services/Utils/clearLocalStorage";
import { CONSTRUCTION_FORM_ROUTE_NAMES } from "@/router/constructionRoutes";
import { ADMIN_NAV_ACTIVE_ROUTE_NAMES } from "@/router/navigationLayout";

import Divider from "@/components/Reusables/Divider/Divider.vue";
import ReportsSelect from "@/features/ReportsSelect";

import MobileMenuItem from "./MobileMenuItem.vue";
import MobileMenuAccordion from "./MobileMenuAccordion.vue";
import ThemeModeToggle from "./ThemeModeToggle.vue";

defineProps<{ lCAdaIsShown?: boolean }>();
defineEmits(["toggleLCAda"]);

const useMobileMenuStore = mobileMenuStore();

const isOpen = computed(() => !useMobileMenuStore.getIsHidden());

const close = () => {
  if (!useMobileMenuStore.getIsHidden()) useMobileMenuStore.toggleIsHidden();
};

const toggle = () => useMobileMenuStore.toggleIsHidden();

const route = useRoute();
const projectRouter = useRouter();

const authStore = useAuthStateStore();
const { isPlatformAdmin } = storeToRefs(authStore);

const projectStore = useProject();
const speckle = useSpeckleStore();
const { project, permissions, hasPhaseA4, hasPhaseA5, models } = storeToRefs(projectStore);
const { enableViewer } = storeToRefs(speckle);

const PROJECT_ROUTE_NAMES = [
  "projectBreakdown",
  "materialMappingView",
  "transportMapping",
  "wasteSorting",
  // Reached from the Reports accordion below — without it the whole project section vanished from
  // the drawer as soon as you opened the LCA report, leaving no way back to the other project pages.
  "projectLcaPdfReportPage",
  "projectVersionHistory",
];
const isOnProjectRoute = computed(() => PROJECT_ROUTE_NAMES.includes(route.name as string));

const projectId = computed(() => String(route.params.id ?? ""));

const hasModelElements = computed(() =>
  (models.value ?? []).some((m) => !!m?.referencedObjectId && m.referencedObjectId !== "UNUSED")
);

const isDashboardRoute = computed(() => route.name === "projectBreakdown");
const isDashboardActive = computed(() => isDashboardRoute.value && !enableViewer.value);
const isModelViewerActive = computed(() => isDashboardRoute.value && enableViewer.value);

const projectLoadedForRoute = computed(() => !!project.value && project.value.id === projectId.value);

// Mirror of ProjectNavigation: keep the active tab honest as soon as the project resolves for the current
// route. A project with no model elements can't be in viewer mode, so drop back to the dashboard
// immediately instead of waiting for the dashboard view to mount and correct it. `models` is set together
// with `project` in the store's loadProject, so gating on projectLoadedForRoute means hasModelElements is
// accurate here and we never kick a model-project out of viewer mode before its models have loaded.
watch(
  [projectLoadedForRoute, hasModelElements],
  ([loaded, projectHasModelElements]) => {
    if (loaded && !projectHasModelElements && enableViewer.value) {
      speckle.enableViewer = false;
    }
  },
  { immediate: true }
);

// Mirrors ProjectNavigation: the only permission this menu still reads. The rest were for the
// Manage accordion's rows, which gate themselves on the settings page now.
const canEditProject = computed(() => permissions.value?.includes("project.edit") ?? false);

// One accordion left, Reports. The union stays a union rather than a boolean because
// MobileMenuAccordion is shared and the Reports one may not be the last.
const openAccordion = ref<"reports" | null>(null);
const toggleAccordion = (key: "reports") => {
  openAccordion.value = openAccordion.value === key ? null : key;
};

const openModelViewer = async () => {
  try {
    await projectRouter.push({ name: "projectBreakdown", params: { id: projectId.value } });
  } catch (e) {
    if ((e as { name?: string })?.name !== "NavigationDuplicated") {
      console.error("[MobileMenu] navigation failed", e);
    }
  }
  speckle.enableViewer = true;
  close();
};

const openDashboard = async () => {
  try {
    await projectRouter.push({ name: "projectBreakdown", params: { id: projectId.value } });
  } catch (e) {
    if ((e as { name?: string })?.name !== "NavigationDuplicated") {
      console.error("[MobileMenu] navigation failed", e);
    }
  }
  speckle.enableViewer = false;
  close();
};

const adminPortal = useAdminPortal();

const showAdminPortal = () => {
  close();
  adminPortal.show();
};

const logout = () => {
  clearLocalStorage();
  router.push("/account/logout");
  useToast().info("Successfully logged out");
  close();
};
</script>

<template>
  <button
    type="button"
    :aria-label="isOpen ? 'Close menu' : 'Open menu'"
    :aria-expanded="isOpen"
    class="focus-ring interactive-active rounded-8 text-type-default hover:bg-app-blue-200/30 flex h-9 w-9 items-center justify-center"
    @click="toggle">
    <component
      :is="isOpen ? X : Menu"
      class="icon-md"
      :stroke-width="1.5" />
  </button>

  <Teleport to="body">
    <Transition name="fly-in-right">
      <div
        v-if="isOpen"
        class="z-modal fixed inset-x-0 top-[57px] bottom-0 overflow-y-auto bg-slate-50"
        role="dialog"
        aria-label="Main menu">
        <nav class="gap-px-4 px-px-16 py-px-16 flex flex-col">
          <MobileMenuItem
            label="Projects"
            to="/"
            :icon-component="Folder"
            :active-names="[
              'projects',
              'projectBreakdown',
              'CreateProject',
              'materialMappingView',
              'transportMapping',
              'wasteSorting',
              'projectVersionHistory',
            ]"
            @navigate="close" />
          <MobileMenuItem
            label="Library"
            to="/library"
            :icon-component="SquareLibrary"
            :active-names="[
              'materialsDatabase',
              'createMaterial',
              'libraryDetail',
              'libraryCompare',
              ...CONSTRUCTION_FORM_ROUTE_NAMES,
            ]"
            @navigate="close" />
          <MobileMenuItem
            v-if="isPlatformAdmin"
            label="Admin Panel"
            to="/admin"
            :icon-component="Shield"
            :active-names="[...ADMIN_NAV_ACTIVE_ROUTE_NAMES]"
            @navigate="close" />

          <template v-if="isOnProjectRoute">
            <Divider
              label="Project"
              spacing="sm" />

            <MobileMenuItem
              label="Dashboard"
              :action="openDashboard"
              :icon-component="ChartPie"
              :active="isDashboardActive" />
            <MobileMenuItem
              v-if="hasModelElements"
              label="3D Model"
              :action="openModelViewer"
              :icon-component="Boxes"
              :active="isModelViewerActive" />
            <MobileMenuItem
              label="Materials"
              :to="`/project/${projectId}/mapping`"
              :icon-component="Layers"
              :active-names="['materialMappingView']"
              @navigate="close" />
            <MobileMenuItem
              v-if="hasPhaseA4"
              label="Transport"
              :to="`/project/${projectId}/transport-mapping`"
              :icon-component="Truck"
              :active-names="['transportMapping']"
              @navigate="close" />
            <MobileMenuItem
              v-if="hasPhaseA5"
              label="Waste"
              :to="`/project/${projectId}/waste`"
              :icon-component="Recycle"
              :active-names="['wasteSorting']"
              @navigate="close" />

            <!-- Mirrors the History item in ProjectNavigation.vue. -->
            <MobileMenuItem
              label="History"
              :to="`/project/${projectId}/history`"
              :icon-component="History"
              :active-names="['projectVersionHistory']"
              @navigate="close" />

            <MobileMenuAccordion
              label="Reports"
              :icon-component="FileText"
              :open="openAccordion === 'reports'"
              @toggle="toggleAccordion('reports')">
              <!-- The LCA report row navigates rather than downloading, so the menu has to close
                   behind it (RTLCA-5435) — otherwise the report opens under the open overlay. -->
              <ReportsSelect
                :building-project-id="projectId"
                @navigate="close" />
            </MobileMenuAccordion>

            <!-- Mirrors the Settings item in ProjectNavigation.vue: one route, not a list of anchors,
                 and last among the project's rows the way it is pinned to the floor there. The page's
                 own rail lists its sections and jumps to them, derived from the wizard's flow rather
                 than repeated here. -->
            <MobileMenuItem
              v-if="canEditProject"
              label="Settings"
              :to="`/project/${projectId}/settings`"
              :icon-component="SlidersHorizontal"
              :active-names="['projectSettings']"
              data-cy="config"
              @navigate="close" />

            <!-- The "Manage" accordion is gone, mirroring ProjectNavigation. Its four rows — Manage
                 Users, Project Settings, Clone Project, Delete Project — are sections of the
                 settings page now, except Project Settings, which was the same destination as the
                 Settings row above and had nothing left to sit beside. -->
          </template>

          <Divider spacing="sm" />

          <!-- Mirrors the Tutorials item in MainNavigation.vue. -->
          <MobileMenuItem
            label="Tutorials"
            to="/tutorials"
            :icon-component="GraduationCap"
            :active-names="['tutorials']"
            @navigate="close" />

          <div class="gap-px-12 px-px-12 py-px-4 flex items-center justify-between">
            <span class="t-small-bold text-type-default">Appearance</span>
            <ThemeModeToggle placement="top-end" />
          </div>

          <MobileMenuItem
            label="Profile"
            :icon-component="UserCircle"
            :action="showAdminPortal" />

          <MobileMenuItem
            label="Logout"
            :icon-component="LogOut"
            :action="logout" />
        </nav>
      </div>
    </Transition>
  </Teleport>
</template>
